import React from 'react';
import LegalPageLayout from '../layouts/LegalPageLayout';
import SEO from '../components/SEO';


const SECTIONS = [
    { id: 'intro',         label: 'Introduction' },
    { id: 'definitions',   label: 'Definitions & Key Terms' },
    { id: 'collection',    label: 'What We Collect' },
    { id: 'usage',         label: 'How We Use Your Information' },
    { id: 'sharing',       label: 'Sharing With Third Parties' },
    { id: 'email',         label: 'How We Use Your Email' },
    { id: 'security',      label: 'Security' },
    { id: 'updates',       label: 'Updating Your Information' },
    { id: 'retention',     label: 'Data Retention' },
    { id: 'contact',       label: 'Contact Us' },
];

const h2 = { fontSize: '1.2rem', fontWeight: '800', color: '#1A1A2E', margin: '0 0 12px', fontFamily: 'Calibri, sans-serif' };
const divider = { borderTop: '2px solid rgba(173,20,87,0.1)', margin: '0 0 24px' };
const p = { color: '#475569', lineHeight: '1.8', marginBottom: '12px', fontFamily: 'Calibri, sans-serif' };
const li = { color: '#475569', lineHeight: '2', fontFamily: 'Calibri, sans-serif' };
const section = { marginBottom: '44px', scrollMarginTop: '90px' };

const PrivacyPolicy = () => (
    <>
        <SEO
            title="Privacy Policy"
            description="Read Oottan's privacy policy to understand how we collect, use, and protect your personal information."
            canonical="/privacy-policy"
        />
        <LegalPageLayout
            title="Privacy Policy"
            updated="10 Oct 2026"
            sections={SECTIONS}
            currentPath="/privacy-policy"
        >
        <div id="intro" style={section}>
            <h2 style={h2}>Introduction</h2>
            <hr style={divider} />
            <p style={p}>
                Hirehike Technologies Private Limited ("We," "our," or "us") is committed to protecting your privacy.
                This Privacy Policy explains how your personal information is collected, used, and disclosed by Oottan.
            </p>
            <p style={p}>
                This Privacy Policy applies to our website{' '}
                <a href="https://oottan.com" style={{ color: '#AD1457' }}>https://oottan.com</a>
                {' '}and its associated subdomains (collectively, our "Service"). By accessing or using our Service,
                you signify that you have read, understood, and agree to our collection, storage, use, and disclosure
                of your personal information as described in this Privacy Policy and our Terms of Service.
            </p>
        </div>

        <div id="definitions" style={section}>
            <h2 style={h2}>Definitions and Key Terms</h2>
            <hr style={divider} />
            <p style={p}>For this Privacy Policy, the following terms are strictly defined:</p>
            <ul style={{ paddingLeft: '20px' }}>
                <li style={li}><strong>Cookie:</strong> Small amount of data generated by a website and saved by your web browser. Used to identify your browser, provide analytics, and remember information such as your language preference or login information.</li>
                <li style={li}><strong>Company:</strong> When this policy mentions "Company," "We," "us," or "our," it refers to <strong>Hirehike Technologies Private Limited, 8/61/1, Kumbavilai, Veeyanoor – 629177, Kanyakumari District, Tamil Nadu, India</strong>.</li>
                <li style={li}><strong>Country:</strong> India, where Oottan or its founders are based.</li>
                <li style={li}><strong>Customer:</strong> The company, organisation, or person that signs up to use the Oottan Service.</li>
                <li style={li}><strong>Device:</strong> Any internet-connected device such as a phone, tablet, or computer that can be used to visit Oottan and use the services.</li>
                <li style={li}><strong>IP Address:</strong> A number assigned to every internet-connected device, often used to identify the location from which a device is connecting.</li>
                <li style={li}><strong>Personal Data:</strong> Any information that directly or indirectly allows for the identification of a natural person.</li>
                <li style={li}><strong>Service:</strong> The service provided by Oottan as described on this platform.</li>
                <li style={li}><strong>Third-party Service:</strong> Advertisers, contest sponsors, promotional and marketing partners, and others who provide content or whose products we think may interest you.</li>
                <li style={li}><strong>Website:</strong> Oottan, accessible via <a href="https://oottan.com" style={{ color: '#AD1457' }}>https://oottan.com</a>.</li>
                <li style={li}><strong>You:</strong> A person or entity registered with Oottan to use the Services.</li>
            </ul>
        </div>

        <div id="collection" style={section}>
            <h2 style={h2}>What Information Do We Collect?</h2>
            <hr style={divider} />
            <p style={p}>We collect information from you when you visit our service, register, place an order, subscribe to our newsletter, respond to a survey, or fill out a form:</p>
            <ul style={{ paddingLeft: '20px' }}>
                <li style={li}>Name / Username</li>
                <li style={li}>Phone Numbers</li>
                <li style={li}>Email Addresses</li>
                <li style={li}>Mailing Addresses</li>
                <li style={li}>Job Titles</li>
                <li style={li}>Billing Addresses</li>
                <li style={li}>Debit / Credit Card Numbers</li>
                <li style={li}>Password</li>
            </ul>
        </div>

        <div id="usage" style={section}>
            <h2 style={h2}>How Do We Use the Information We Collect?</h2>
            <hr style={divider} />
            <p style={p}>Any information we collect may be used in one of the following ways:</p>
            <ul style={{ paddingLeft: '20px' }}>
                <li style={li}>To personalise your experience and respond to your individual needs</li>
                <li style={li}>To improve our service based on information and feedback we receive</li>
                <li style={li}>To improve customer service and respond to requests more effectively</li>
                <li style={li}>To process transactions</li>
                <li style={li}>To administer contests, promotions, surveys, or other site features</li>
                <li style={li}>To send periodic emails</li>
            </ul>
        </div>

        <div id="sharing" style={section}>
            <h2 style={h2}>Do We Share the Information We Collect With Third Parties?</h2>
            <hr style={divider} />
            <p style={p}>
                We may share information that we collect, both personal and non-personal, with third parties such as
                advertisers, contest sponsors, promotional and marketing partners, and others who provide our content or
                whose products or services we think may interest you. We may also share it with our current and future
                affiliated companies and business partners.
            </p>
            <p style={p}>
                We may engage trusted third-party service providers to perform functions such as hosting and maintaining
                our servers, database storage and management, e-mail management, credit card processing, and customer
                service. We will likely share your personal information with these third parties to enable them to
                perform these services for us and for you.
            </p>
        </div>

        <div id="email" style={section}>
            <h2 style={h2}>How Do We Use Your Email Address?</h2>
            <hr style={divider} />
            <p style={p}>
                By submitting your email address on this website, you agree to receive emails from us. You can cancel
                your participation in any email list at any time by clicking the opt-out link included in the respective
                email. We only send emails to people who have authorised us to contact them. We do not send unsolicited
                commercial emails.
            </p>
        </div>

        <div id="security" style={section}>
            <h2 style={h2}>Is the Information Collected Through Our Service Secure?</h2>
            <hr style={divider} />
            <p style={p}>
                We take precautions to protect the security of your information. We have physical, electronic, and
                managerial procedures to help safeguard, prevent unauthorised access, maintain data security, and
                correctly use your information. However, no security system is infallible. We cannot guarantee the
                absolute security of your personal information.
            </p>
        </div>

        <div id="updates" style={section}>
            <h2 style={h2}>Can I Update or Correct My Information?</h2>
            <hr style={divider} />
            <p style={p}>
                You have the right to request updates or corrections to the information we hold about you. You can
                contact us to (1) update or correct your personally identifiable information, (2) change your
                preferences with respect to communications, or (3) delete the personally identifiable information
                maintained about you on our systems by cancelling your account.
            </p>
        </div>

        <div id="retention" style={section}>
            <h2 style={h2}>How Long Do We Keep Your Information?</h2>
            <hr style={divider} />
            <p style={p}>
                We keep your information only as long as we need it to provide service to you and fulfil the purposes
                described in this policy. When we no longer need to use your information and there is no need for us
                to keep it to comply with our legal or regulatory obligations, we will either remove it from our
                systems or depersonalise it so that we cannot identify you.
            </p>
        </div>

        <div id="contact" style={section}>
            <h2 style={h2}>Contact Us</h2>
            <hr style={divider} />
            <p style={p}>Don't hesitate to contact us if you have any questions about this Privacy Policy.</p>
            <ul style={{ paddingLeft: '20px' }}>
                <li style={li}>Email: <strong>info@oottan.com</strong></li>
                <li style={li}>Phone: <strong>04651-215020</strong></li>
                <li style={li}>Address: <strong>8/61/1, Kumbavilai, Veeyanoor – 629177, Kanyakumari District, Tamil Nadu, India</strong></li>
            </ul>
        </div>
        </LegalPageLayout>
    </>
);

export default PrivacyPolicy;
